import React from "react";
import { createBaseBlock } from "./BaseBlock";
import styles from "../../components/topicfeed.module.css";
import { KeyMetricsGrid } from "../components/KeyMetricsGrid";

interface Metric {
  metric: string;
  current_value: number;
  previous_value?: number;
  percent_change?: string;
  unit?: string;
  commentary?: string;
  evidence?: string;
}

interface KeyMetricsData {
  metrics: Metric[];
  title?: string;
  subtitle?: string;
}

// KeyMetrics Grid Block Recipe
const keyMetricsRecipe = {
  id: "key-metrics-block",
  name: "Key Metrics Block",
  globalKey: [],
  prefix: `[
        {
          "metric": "string — e.g. 'PR Cycle Time', 'Issue Comment Volume'",
          "current_value": "number",
          "previous_value": "number",
          "percent_change": "string — e.g. '+24%'",
          "unit": "string — e.g. 'days', 'comments'",
          "commentary": "string — optional summary of what caused the change",
          "evidence": "string — optional summary of what caused the change"
        }
    ]`,
  format: {
    type: "json",
    fields: ["metric"],
    constraints: [{ field: "metric", type: "string", required: true }],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as KeyMetricsData;
          return Boolean(
            Array.isArray(parsed.metrics) &&
              parsed.metrics.length > 0 &&
              parsed.metrics.every(
                (metric) =>
                  metric.metric &&
                  metric.current_value !== undefined &&
                  metric.current_value !== null
              )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// KeyMetrics Grid Block Parser
function keyMetricsParser(data: any) {
  try {
    return {
      data: data[keyMetricsBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse key metrics data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// KeyMetrics Grid Block Variants
const keyMetricsVariants = [
  {
    id: "default",
    name: "Default",
    settings: {
      keyMetricsGridVariant: "simpleCard" as const,
      maxItems: 3,
    },
    description: "Standard grid layout",
  },
  {
    id: "card",
    name: "Card",
    settings: {
      keyMetricsGridVariant: "softCard" as const,
      maxItems: 3,
    },
    description: "Card layout",
  },
  {
    id: "modern",
    name: "Modern",
    settings: {
      keyMetricsGridVariant: "modern" as const,
      maxItems: 3,
    },
    description: "Modern layout",
  },
  {
    id: "minimal",
    name: "Minimal",
    settings: {
      keyMetricsGridVariant: "softRow" as const,
      maxItems: 3,
    },
    description: "Minimal layout",
  },
  {
    id: "low-profile",
    name: "Low Profile",
    settings: {
      keyMetricsGridVariant: "simpleRow" as const,
      maxItems: 3,
    },
    description: "Low profile layout",
  },
  {
    id: "horizontal-compact",
    name: "Horizontal Compact",
    settings: {
      keyMetricsGridVariant: "horizontal-compact" as const,
      maxItems: 3,
    },
    description: "Horizontal compact layout",
  },
];

// KeyMetrics Grid Block Renderer
function KeyMetricsBlockRenderer(props: any) {
  const { data, variant, settings, className, style } = props;
  const metrics = data[keyMetricsBlock.id];
  const variantSettings = keyMetricsVariants.find(
    (v) => v.id === variant
  )?.settings;

  return (
    <>
      {metrics && metrics.length > 0 && (
        <div
          className={`${styles.metrics} vzbl-panel-container`}
          style={{
            paddingTop: "var(--padding-3)",
            paddingBottom: "var(--padding-3)",
            width: "100%",
            display: "block",
          }}
        >
          <KeyMetricsGrid
            metrics={metrics}
            maxItems={variantSettings?.maxItems}
            variant={
              variantSettings?.keyMetricsGridVariant || ("default" as const)
            }
          />
        </div>
      )}
    </>
  );
}

// Create KeyMetrics Grid Block
const keyMetricsBlock = createBaseBlock({
  id: "key-metrics",
  name: "Key Metrics Block",
  description:
    "Displays a grid of key metrics with optional trends and descriptions",
  recipe: keyMetricsRecipe,
  variants: keyMetricsVariants,
  parser: keyMetricsParser,
  renderer: KeyMetricsBlockRenderer,
});

// Export the block for direct use
export { keyMetricsBlock };
